import { useEffect, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ongletActif } from '../lib/ongletActif';
import { useAuthStore } from '../store/auth.store';

/**
 * Barre d'onglets des pages connectées du portail (01/11/2026) : outils,
 * espace, dossiers, abonnements, compte. Elle n'apparaît qu'une fois la
 * session connue — jamais sur un écran qui va renvoyer à la connexion.
 */

/** Icônes au trait, 24 × 24, couleur du texte (aucune bibliothèque d'icônes au portail). */
const Icone = ({ children }: { children: ReactNode }) => (
  <svg
    className="portail-nav__icone"
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

interface Onglet {
  vers: string;
  libelle: string;
  /** Libellé sur téléphone, où les cinq onglets tiennent sur une ligne. */
  court: string;
  icone: ReactNode;
  /** Préfixes d'adresse qui rendent l'onglet actif, en plus de `vers`. */
  aussi?: string[];
}

const ONGLETS: Onglet[] = [
  {
    vers: '/produits',
    libelle: 'Mes outils',
    court: 'Outils',
    icone: (
      <Icone>
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
      </Icone>
    ),
  },
  {
    vers: '/espace',
    libelle: 'Mon espace',
    court: 'Espace',
    aussi: ['/invitation'],
    icone: (
      <Icone>
        <circle cx="9" cy="8" r="3.2" />
        <path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" />
        <circle cx="17" cy="9" r="2.4" />
        <path d="M16.5 14.6c2.6.2 4.5 2.1 4.5 4.9" />
      </Icone>
    ),
  },
  {
    vers: '/dossiers/nouveau',
    libelle: 'Dossiers',
    court: 'Dossiers',
    aussi: ['/dossiers', '/bienvenue'],
    icone: (
      <Icone>
        <path d="M3 7.5A1.5 1.5 0 0 1 4.5 6H9l2 2h8.5A1.5 1.5 0 0 1 21 9.5v8a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5z" />
        <path d="M12 11.5v4M10 13.5h4" />
      </Icone>
    ),
  },
  {
    vers: '/abonnements',
    libelle: 'Abonnements',
    court: 'Abonnements',
    icone: (
      <Icone>
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="M3 10h18M7 15h4" />
      </Icone>
    ),
  },
  {
    vers: '/compte',
    libelle: 'Mon compte',
    court: 'Compte',
    icone: (
      <Icone>
        <circle cx="12" cy="8.5" r="3.8" />
        <path d="M4.5 20c.8-3.6 3.8-5.8 7.5-5.8s6.7 2.2 7.5 5.8" />
      </Icone>
    ),
  },
];

export function PortailNav() {
  const { user, fetchMe, logout } = useAuthStore();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Les pages relisent déjà la session ; la requête est partagée (magasin).
    if (!useAuthStore.getState().user) void fetchMe();
  }, [fetchMe]);

  if (!user || user.mfaRequired) return null;

  return (
    <nav className="portail-nav" aria-label="Portail SEmply">
      <ul className="portail-nav__liste">
        {ONGLETS.map((o) => {
          const actif = ongletActif([o.vers, ...(o.aussi ?? [])], pathname);
          return (
            <li key={o.vers}>
              <Link
                to={o.vers}
                className={`portail-nav__onglet${actif ? ' is-actif' : ''}`}
                aria-current={actif ? 'page' : undefined}
              >
                {o.icone}
                <span className="portail-nav__long">{o.libelle}</span>
                <span className="portail-nav__court" aria-hidden="true">
                  {o.court}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        className="portail-nav__sortie"
        onClick={() => void logout().then(() => navigate('/login'))}
      >
        <Icone>
          <path d="M14 4h4.5A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5H14" />
          <path d="M10 8l-4 4 4 4M6 12h10" />
        </Icone>
        <span>Déconnexion</span>
      </button>
    </nav>
  );
}
